// REST client for the shop and the admin Orders tab.
import type {
  CheckoutRequest,
  CheckoutResult,
  CustomPackCheck,
  OrderStatus,
  ProductKind,
  PublicOrder,
  ShopCatalog,
  ShopProduct,
} from '@shared/types';
import { ApiError } from '../../lib/api';
import { getAdminToken, saveAdminToken } from '../../lib/storage';

async function request<T>(path: string, init: RequestInit & { json?: unknown; admin?: boolean } = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (init.json !== undefined) headers['content-type'] = 'application/json';
  if (init.admin) headers.authorization = `Bearer ${getAdminToken()}`;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers, body: init.json !== undefined ? JSON.stringify(init.json) : init.body });
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check your connection.');
  }
  const body = res.headers.get('content-type')?.includes('application/json') ? await res.json() : null;
  if (!res.ok) {
    if (res.status === 401 && init.admin) saveAdminToken(null);
    throw new ApiError(res.status, (body as { error?: string } | null)?.error ?? `Request failed (${res.status}).`);
  }
  return body as T;
}

export const shopApi = {
  catalog: () => request<ShopCatalog>('/shop/products'),
  product: (slug: string) => request<{ checkout: ShopCatalog['checkout']; product: ShopProduct }>(`/shop/products/${encodeURIComponent(slug)}`),
  checkPack: (code: string) => request<CustomPackCheck>(`/shop/custom-pack/${encodeURIComponent(code.trim())}`),
  checkout: (input: CheckoutRequest) => request<CheckoutResult>('/shop/checkout', { method: 'POST', json: input }),
  order: (publicId: string) => request<PublicOrder>(`/shop/orders/${encodeURIComponent(publicId)}`),
};

export interface ShippingDetails {
  name: string;
  phone: string;
  address: { line1: string; line2: string; city: string; state: string; postal_code: string; country: string };
}

export interface AdminOrder {
  id: string;
  publicId: string;
  status: OrderStatus;
  preorder: boolean;
  product: { id: string; slug: string; name: string; kind: ProductKind; packSlugs: string[] };
  quantity: number;
  amountCents: number;
  currency: string;
  email: string;
  customerName: string;
  shipping: ShippingDetails | null;
  customPackCode: string | null;
  stripeSessionId: string | null;
  stripePaymentIntent: string | null;
  tracking: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  customPack?: CustomPackCheck | null;
}

export type OrderFilter = OrderStatus | 'preorder' | '';

export const shopAdmin = {
  orders: (status: OrderFilter) =>
    request<{ orders: AdminOrder[]; counts: Record<string, number> }>(`/admin/shop/orders${status ? `?status=${status}` : ''}`, { admin: true }),
  order: (id: string) => request<AdminOrder>(`/admin/shop/orders/${id}`, { admin: true }),
  updateOrder: (id: string, patch: { status?: OrderStatus; tracking?: string; notes?: string }) =>
    request<AdminOrder>(`/admin/shop/orders/${id}`, { method: 'PATCH', json: patch, admin: true }),
  products: () => request<ShopProduct[]>('/admin/shop/products', { admin: true }),
  updateProduct: (id: string, patch: Partial<Pick<ShopProduct, 'name' | 'description' | 'priceCents' | 'active' | 'sortOrder'>>) =>
    request<{ ok: true }>(`/admin/shop/products/${id}`, { method: 'PATCH', json: patch, admin: true }),
  /** The print file needs the admin token, so fetch it and hand the browser a blob to save. */
  downloadPrint: async (order: AdminOrder, opts: { bleed?: boolean; backs?: boolean }) => {
    const q = new URLSearchParams();
    if (opts.bleed) q.set('bleed', '1');
    if (opts.backs) q.set('backs', '1');
    const res = await fetch(`/api/admin/shop/orders/${order.id}/print.pdf?${q}`, { headers: { authorization: `Bearer ${getAdminToken()}` } });
    if (!res.ok) throw new ApiError(res.status, res.status === 401 ? 'Please log in again.' : 'Could not make the print file.');
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `completemasti-${order.publicId}.pdf`;
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    return { pages: Number(res.headers.get('x-print-pages')) || 0, warnings: Number(res.headers.get('x-print-warnings')) || 0 };
  },
};

export function formatPrice(cents: number, currency = 'usd'): string {
  const whole = cents % 100 === 0;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  }).format(cents / 100);
}

export const STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pending',
  paid: 'Paid',
  in_production: 'In production',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};
